import { ref, computed } from 'vue';

// Per-slot character validation rules
// Slots 0-2: alphabetical only
// Slots 3-4: numerical only
// Slot 5:   alphanumerical
const SLOT_RULES: RegExp[] = [
  /^[A-Za-z]$/,
  /^[A-Za-z]$/,
  /^[A-Za-z]$/,
  /^[0-9]$/,
  /^[0-9]$/,
  /^[A-Za-z0-9]$/,
];

export type KeyboardMode = 'alpha' | 'numeric' | 'alphanumeric';

export function useCodeInput() {
  const slots = ref<(string | null)[]>(Array(6).fill(null));
  const cursor = ref(0);

  // The keyboard layout is automatically driven by cursor position,
  // but can be manually overridden by the user via the keyboard toggle.
  const autoMode = computed<KeyboardMode>(() => {
    if (cursor.value <= 2) return 'alpha';
    if (cursor.value <= 4) return 'numeric';
    return 'alphanumeric';
  });

  const manualMode = ref<KeyboardMode | null>(null);
  const keyboardMode = computed<KeyboardMode>(() => manualMode.value ?? autoMode.value);

  // null until all 6 slots are filled
  const code = computed<string | null>(() =>
    slots.value.every((s) => s !== null)
      ? (slots.value as string[]).join('').toUpperCase()
      : null,
  );

  function inputChar(char: string): void {
    if (cursor.value >= 6) return;
    const rule = SLOT_RULES[cursor.value];
    if (!rule?.test(char)) return;
    slots.value[cursor.value] = char.toUpperCase();
    cursor.value++;
    // Reset manual mode override when cursor advances (new slot may have different rules)
    manualMode.value = null;
  }

  function backspace(): void {
    if (cursor.value > 0) {
      cursor.value--;
      slots.value[cursor.value] = null;
      manualMode.value = null;
    }
  }

  function reset(): void {
    slots.value = Array(6).fill(null);
    cursor.value = 0;
    manualMode.value = null;
  }

  function setKeyboardMode(mode: KeyboardMode): void {
    manualMode.value = mode;
  }

  /** Fill slots from a transcribed string, skipping chars that violate slot rules. */
  function fillFromTranscription(raw: string): void {
    reset();
    const chars = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    for (const char of chars) {
      if (cursor.value >= 6) break;
      inputChar(char);
    }
  }

  return {
    slots,
    cursor,
    keyboardMode,
    code,
    inputChar,
    backspace,
    reset,
    setKeyboardMode,
    fillFromTranscription,
  };
}
